import { getEndpointForFetch } from "../../data-layer/fetch-api";
import { Either } from "fp-ts/es6/Either";
import { either } from "fp-ts";
import { SalesforceUserProfileT } from "../../io/salesforce/fetch-user-profile";

export type UserAuthToken = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: number;
  scope: string;
  token_type: "Bearer";
};

export const AuthClient = {
  login: (
    { code, state }: { code: string; state: string },
    anonymousAccessToken: string,
  ): Promise<UserAuthToken | null> => {
    return fetch(`/storefront/login`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${anonymousAccessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code,
        state,
      }),
    })
      .then(res => res.json())
      .catch(err => {
        console.error("error", err);
        return null;
      });
  },

  fetchSalesforceUserProfile: (userAuthToken: UserAuthToken): Promise<Either<string, SalesforceUserProfileT>> => {
    const accessToken = userAuthToken.access_token;

    return fetch(`/auth/user-profile`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then(res => {
      if (res.status === 401) {
        return Promise.resolve(either.left("unauthorized"));
      } else {
        return res.json().then(json => either.right(json));
      }
    });
  },

  getAnonymousAccessToken: (baseUrl?: URL): Promise<UserAuthToken> => {
    let searchParams = new URLSearchParams();
    searchParams.set("client_id", "frontend-app");
    searchParams.set("grant_type", "client_credentials");

    return fetch(getEndpointForFetch("/auth/anonymous/token", baseUrl), {
      method: "POST",
      body: searchParams,
    })
      .then(res => res.json())
      .then(json => {
        return {
          access_token: json.access_token,
          expires_in: json.expires_in,
          expires_at: json.expires_at,
          refresh_token: json.refresh_token,
          scope: json.scope,
          token_type: json.token_type,
        };
      });
  },

  refreshAuthToken: (token: UserAuthToken): Promise<UserAuthToken> => {
    let searchParams = new URLSearchParams();
    searchParams.set("client_id", "frontend-app");
    searchParams.set("grant_type", "refresh_token");
    searchParams.set("refresh_token", token.refresh_token);

    return fetch("/auth/token", {
      method: "POST",
      body: searchParams,
    })
      .then(res => res.json())
      .then(json => {
        return {
          access_token: json.access_token,
          refresh_token: json.refresh_token || token.refresh_token, // if it's not preset in the response, current one is still valid
          expires_in: json.expires_in,
          expires_at: json.expires_at,
          scope: json.scope || token.scope,
          token_type: json.token_type,
        };
      });
  },
};
